import {
  Component,
  ViewEncapsulation,
  input,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  effect,
  WritableSignal,
  signal,
} from '@angular/core';
import { SavedViewActionsComponent } from '@/app/modules/saved-views/saved-view-actions/saved-view-actions.component';
import { BreadcrumbService } from '@/app/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { SavedViewsSaveAsDialogComponent } from './saved-views-save-as-dialog.component';
import { SavedViewsSaveDialogComponent } from './saved-views-save-dialog.component';
import { SavedViewsFiltersDialogComponent } from '@/app/modules/saved-views/saved-views/saved-views-filters-dialog.component';
import { SavedViewsManagerDialogComponent } from '@/app/modules/saved-views/saved-views/saved-views-manager-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LogService, Logger } from '@/app/core/services/log.service';
import { Router, NavigationStart } from '@angular/router';

export const SAVE_DIALOG_LOCAL_STORAGE_KEY = 'show_saved_views_dialog';

@Component({
  selector: 'rdo-saved-views',
  imports: [SavedViewActionsComponent],
  providers: [LogService],
  template: `
    <ng-template #actions>
      <rdo-saved-view-actions
        [gridName]="gridName()"
        (saveClick)="handleSaveClick($event)"
        (saveAsClick)="handleSaveAsClick($event)"
      />
    </ng-template>
  `,
  standalone: true,
  encapsulation: ViewEncapsulation.Emulated,
})
export class SavedViewsComponent implements OnInit, OnDestroy {
  @ViewChild('actions') actionsTemplate: TemplateRef<any>;
  public pageName = input('');
  public gridName = input('');
  private portal: TemplatePortal;
  private actionsPortalId = 'savedViewActionsPortal';
  private isFiltersDialogOpen: WritableSignal<boolean> = signal(false);
  private isManagementDialogOpen: WritableSignal<boolean> = signal(false);
  private routerSubscription: any;
  private logger: Logger;

  constructor(
    private savedViewsService: SavedViewsService,
    private breadcrumbService: BreadcrumbService,
    private materialDialog: MatDialog,
    private viewContainerRef: ViewContainerRef,
    private logService: LogService,
    private router: Router // Inject Router
  ) {
    this.logger = this.logService.getModuleLogger(
      'SavedViews',
      'SavedViewsComponent',
      false
    );
    effect(() => {
      if (
        this.savedViewsService.isConfirmFiltersDialogOpen() &&
        this.isFiltersDialogOpen() === false
      ) {
        this.openFiltersDialog();
      } else if (
        !this.savedViewsService.isConfirmFiltersDialogOpen() &&
        this.isFiltersDialogOpen()
      ) {
        this.closeFiltersDialog();
      }
    });

    effect(() => {
      if (this.savedViewsService.isManagementDialogOpen()) {
        this.openManagementDialog();
      } else if (
        !this.savedViewsService.isManagementDialogOpen() &&
        this.isManagementDialogOpen()
      ) {
        this.isManagementDialogOpen.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.logger.info(
      'saved-views.component.ngOnInit  called',
      this.gridName(),
      this.pageName(),
      'default view name',
      this.savedViewsService.defaultViewName()
    );
    setTimeout(() => {
      if (this.actionsTemplate) {
        this.portal = new TemplatePortal(
          this.actionsTemplate,
          this.viewContainerRef
        );
        // Register with unique ID
        this.breadcrumbService.registerPortal(
          this.actionsPortalId,
          this.portal
        );
      }
    });

    // Subscribe to router events to close dialogs
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.materialDialog.closeAll();
        this.isFiltersDialogOpen.set(false);
        this.isManagementDialogOpen.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.breadcrumbService.unregisterPortal(this.actionsPortalId);
    this.savedViewsService.reset();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  async handleSaveClick(event: Event): Promise<void> {
    this.logger.info(
      'SavedViewsComponent.handleSaveClick called',
      this.gridName()
    );

    const showDialog = localStorage.getItem(SAVE_DIALOG_LOCAL_STORAGE_KEY);

    if (!showDialog || showDialog === 'true') {
      const dialogConfig = this.getDialogConfig();
      dialogConfig.id = 'SavedViewsSaveDialog';

      // Pass the save callback to the dialog
      dialogConfig.data = {
        onSave: async (data: any) => {
          return await this.savedViewsService.patch();
        },
      };

      this.materialDialog
        .open(SavedViewsSaveDialogComponent, dialogConfig)
        .afterClosed()
        .subscribe((dialogResponse) => {
          // Handle dialog response
          if (dialogResponse && dialogResponse.success) {
            console.log('Save dialog completed successfully:', dialogResponse);
          } else if (dialogResponse) {
            console.log('Save dialog closed:', dialogResponse);
          } else {
            console.log('Save dialog cancelled');
          }
        });
    } else {
      return await this.savedViewsService.patch();
    }
  }

  handleSaveAsClick(event: Event): void {
    this.logger.info(
      'SavedViewsComponent.handleSaveAsClick called',
      this.gridName()
    );
    const dialogConfig = this.getDialogConfig();
    dialogConfig.id = 'SavedViewsSaveAsDialog';

    // Pass the save callback to the dialog
    dialogConfig.data = {
      onSave: async (reportName: string, setAsDefault: boolean) => {
        return await this.savedViewsService.saveAs({
          reportName,
          setAsDefault,
        });
      },
    };

    this.materialDialog
      .open(SavedViewsSaveAsDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((dialogResponse) => {
        // Handle dialog response
        if (dialogResponse && dialogResponse.success) {
          console.log('Save As dialog completed successfully:', dialogResponse);
        } else if (dialogResponse) {
          console.log('Save As dialog closed:', dialogResponse);
        } else {
          console.log('Save As dialog cancelled');
        }
      });
  }

  private getDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';
    dialogConfig.height = '250px';
    dialogConfig.restoreFocus = true;
    return dialogConfig;
  }

  private openFiltersDialog(): void {
    this.logger.info('SavedViewsComponent.openFiltersDialog called');
    this.isFiltersDialogOpen.set(true);
    const dialogConfig = this.getDialogConfig();
    dialogConfig.id = 'SavedViewsFiltersDialog';
    this.materialDialog
      .open(SavedViewsFiltersDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((dialogResponse) => {
        this.logger.info('SavedViewsComponent.openFiltersDialog dialog closed');
        this.isFiltersDialogOpen.set(false);
        // Handle dialog response
        if (dialogResponse && dialogResponse.success) {
          this.logger.info(
            'Filters dialog completed successfully:',
            dialogResponse
          );
        } else {
          // Only cancel/reject when user actually dismissed without accepting
          this.savedViewsService.cancelNewView();
          this.logger.info('Filters dialog cancelled');
        }
      });
  }

  private openManagementDialog(): void {
    this.logger.info('SavedViewsComponent.openManagementDialog called');
    this.isManagementDialogOpen.set(true);
    const dialogConfig = this.getDialogConfig();
    dialogConfig.id = 'SavedViewsManagerDialog';
    dialogConfig.width = '600px';
    dialogConfig.height = '420px';
    dialogConfig.data = {
      pageName: this.pageName(),
      gridName: this.gridName(),
    };
    this.materialDialog
      .open(SavedViewsManagerDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((dialogResponse) => {
        this.logger.info(
          'SavedViewsComponent.openManagementDialog dialog closed'
        );
        this.savedViewsService.isManagementDialogOpen.set(false);
        // Handle dialog response
        if (dialogResponse && dialogResponse.success) {
          this.logger.info(
            'Management dialog completed successfully:',
            dialogResponse
          );
        } else if (dialogResponse) {
          this.logger.info('Management dialog closed:', dialogResponse);
        } else {
          this.logger.info('Management dialog cancelled');
        }
      });
  }

  private closeFiltersDialog(): void {
    this.isFiltersDialogOpen.set(false);
  }
}
